import { useState, useEffect } from 'react';

export interface AfterHoursStockData {
  stock_code: string;
  stock_name: string;
  price: number;
  change_rate: number;
  volume: number;
  timestamp: string;
}

export interface AfterHoursResponse {
  success: boolean;
  data: AfterHoursStockData[];
  count: number;
  startTime: string;
  endTime: string;
  timestamp: string;
}

export const useRankingFluctuationAfterHours = () => {
  const [data, setData] = useState<AfterHoursStockData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  const fetchAfterHoursData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Redis 엔드포인트를 사용하여 시간외 데이터 가져오기 (2시간 제한)
      const response = await fetch(
        `/mercury-backend/ranking-fluctuation-daily?hours=2&keyPrefix=kiwoom:after-hours:ranking`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: AfterHoursResponse = await response.json();
      
      if (result.success) {
        setData(result.data);
      } else {
        throw new Error('Failed to fetch after-hours data');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('Error fetching after-hours data:', err);
    } finally {
      setLoading(false);
    }
  };

  // 초기 로드만 수행하고, 실패 시 자동 재시도하지 않음
  useEffect(() => {
    if (!hasInitialized) {
      setHasInitialized(true);
      fetchAfterHoursData();
    }
  }, [hasInitialized]);

  // 데이터를 시간별로 그룹화하는 함수 (Redis 데이터 구조에 맞게 수정)
  const getDataByHour = () => {
    const hourlyData: { [hour: number]: AfterHoursStockData[] } = {};
    
    // data가 null이거나 undefined인 경우 빈 배열로 처리
    if (data && Array.isArray(data)) {
      data.forEach(item => {
        // timestamp에서 시간 추출
        const date = new Date(item.timestamp);
        const hour = date.getHours();
        
        if (!hourlyData[hour]) {
          hourlyData[hour] = [];
        }
        hourlyData[hour].push(item);
      });
    }

    // 0시부터 23시까지 모든 시간에 대해 빈 배열 초기화
    for (let hour = 0; hour < 24; hour++) {
      if (!hourlyData[hour]) {
        hourlyData[hour] = [];
      }
    }

    return hourlyData;
  };

  // 시간별 평균 등락률 계산
  const getAverageChangeRateByHour = () => {
    const hourlyData = getDataByHour();
    const averageRates: { [hour: number]: number } = {};

    for (let hour = 0; hour < 24; hour++) {
      const stocks = hourlyData[hour];
      if (stocks.length > 0) {
        const totalRate = stocks.reduce((sum, stock) => sum + stock.change_rate, 0);
        averageRates[hour] = totalRate / stocks.length;
      } else {
        averageRates[hour] = 0;
      }
    }

    return averageRates;
  };

  // 시간별 총 거래량 계산
  const getTotalVolumeByHour = () => {
    const hourlyData = getDataByHour();
    const totalVolumes: { [hour: number]: number } = {};

    for (let hour = 0; hour < 24; hour++) {
      const stocks = hourlyData[hour];
      totalVolumes[hour] = stocks.reduce((sum, stock) => sum + stock.volume, 0);
    }

    return totalVolumes;
  };

  return {
    data,
    loading,
    error,
    refetch: fetchAfterHoursData,
    getDataByHour,
    getAverageChangeRateByHour,
    getTotalVolumeByHour,
  };
};
